import React, { useState } from "react";
import { toast } from "sonner";
import Layout from "../components/Layout";
import { api, errorMessage } from "../lib/api";

const EVENT_TYPES = ["Wedding", "Birthday", "Graduation", "Party", "School Event", "Other"];

const PRICING = [
  { label: "6 Cookies", price: "$25" },
  { label: "12 Cookies", price: "$40" },
  { label: "24 Cookies", price: "Starting at $85" },
  { label: "50+ Cookies", price: "Custom Quote" },
];

export default function CustomOrdersPage() {
  const [form, setForm] = useState({
    name: "", event_type: "Birthday", event_date: "", quantity: "",
    preferred_flavors: "", custom_writing: "", theme_notes: "", inspiration_photos: "", additional_notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/custom-orders", form);
      toast.success("Request submitted! We'll be in touch.");
      setForm({ name: "", event_type: "Birthday", event_date: "", quantity: "", preferred_flavors: "", custom_writing: "", theme_notes: "", inspiration_photos: "", additional_notes: "" });
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setSubmitting(false); }
  };

  return (
    <Layout>
      <section className="page-section" data-testid="custom-orders-page">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-sm uppercase tracking-[0.25em] font-bold text-white/85 drop-shadow">Special Events</p>
            <h1 className="font-display font-extrabold text-4xl md:text-6xl mt-2 text-white drop-shadow-lg">Custom Cookie Orders</h1>
            <p className="text-white/90 mt-4 max-w-xl mx-auto">For weddings, birthdays, graduations, parties, and school events.</p>
          </div>

          <div className="card-soft p-6 md:p-8 mb-8">
            <h3 className="font-display font-extrabold text-xl mb-4">Pricing Guide</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {PRICING.map(p => (
                <div key={p.label} className="p-4 rounded-2xl bg-[#FFF8F2] text-center">
                  <p className="font-display font-bold text-base">{p.label}</p>
                  <p className="text-sm text-[#FF1F87] font-extrabold mt-1">{p.price}</p>
                </div>
              ))}
            </div>
            <div className="ss-gradient text-white text-center text-sm font-bold py-3 px-4 rounded-full mt-6">
              Custom orders require at least 30 days notice
            </div>
          </div>

          <form onSubmit={submit} className="card-soft p-6 md:p-8 space-y-4" data-testid="custom-order-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Your Name" required>
                <input className="input-base" value={form.name} onChange={e => set("name", e.target.value)} required data-testid="custom-name" />
              </Field>
              <Field label="Event Type" required>
                <select className="input-base" value={form.event_type} onChange={e => set("event_type", e.target.value)} data-testid="custom-event-type">
                  {EVENT_TYPES.map(t => <option key={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Event Date" required>
                <input type="date" className="input-base" value={form.event_date} onChange={e => set("event_date", e.target.value)} required data-testid="custom-event-date" />
              </Field>
              <Field label="Quantity Needed" required>
                <input className="input-base" value={form.quantity} onChange={e => set("quantity", e.target.value)} placeholder="e.g., 24" required data-testid="custom-quantity" />
              </Field>
            </div>
            <Field label="Preferred Flavors">
              <input className="input-base" value={form.preferred_flavors} onChange={e => set("preferred_flavors", e.target.value)} placeholder="e.g., Chocolate chip, snickerdoodle" data-testid="custom-flavors" />
            </Field>
            <Field label="Custom Writing">
              <input className="input-base" value={form.custom_writing} onChange={e => set("custom_writing", e.target.value)} placeholder="e.g., Happy Birthday Sara!" data-testid="custom-writing" />
            </Field>
            <Field label="Theme Notes">
              <textarea className="input-base min-h-[80px]" value={form.theme_notes} onChange={e => set("theme_notes", e.target.value)} placeholder="Colors, decorations, theme..." data-testid="custom-theme" />
            </Field>
            <Field label="Inspiration Photos (links)">
              <input className="input-base" value={form.inspiration_photos} onChange={e => set("inspiration_photos", e.target.value)} placeholder="Paste URLs, separated by commas" data-testid="custom-inspiration" />
            </Field>
            <Field label="Additional Notes">
              <textarea className="input-base min-h-[80px]" value={form.additional_notes} onChange={e => set("additional_notes", e.target.value)} data-testid="custom-notes" />
            </Field>
            <p className="text-xs text-[#4B5563]">No payment collected — this is a request submission only.</p>
            <button type="submit" disabled={submitting} className="btn-pink w-full" data-testid="custom-submit-btn">
              {submitting ? "SENDING..." : "SUBMIT REQUEST"}
            </button>
          </form>
        </div>
      </section>
    </Layout>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-widest font-bold text-[#4B5563]">{label}{required && <span className="text-[#FF1F87] ml-1">*</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
